import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { useAuthStore, useLanguageStore } from '../store';
import { useTranslation } from '../i18n/translations';
import { toast } from 'sonner';
import { getRecaptchaToken } from '../lib/recaptcha';
import { trackEvent } from '../lib/analytics';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Модальное окно запроса оптовых/B2B цен.
 * Все надписи, плейсхолдеры и статусы полностью локализованы и связаны со словарем CMS.
 */
const TradePricingRequestModal = ({ open, onClose }) => {
  const { language } = useLanguageStore();
  const { t } = useTranslation(language);
  const user = useAuthStore((s) => s.user);
  const token = useAuthStore((s) => s.token);

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    project_name: '',
    products_of_interest: '',
    quantity: '',
    target_deadline: '',
    client_company: '',
    client_country_city: '',
    budget_range: '',
    notes: '',
  });

  const l = t.projectSpec || {};

  const handleChange = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const reset = () => {
    setForm({
      project_name: '', products_of_interest: '', quantity: '',
      target_deadline: '', client_company: '', client_country_city: '',
      budget_range: '', notes: '',
    });
    setSubmitted(false);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose?.();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.project_name.trim() || !form.products_of_interest.trim() || !form.quantity.trim()) {
      toast.error('Project name, products and quantity are required.');
      return;
    }
    setSubmitting(true);
    try {
      const recaptcha_token = await getRecaptchaToken('trade_pricing_requested');
      const res = await fetch(`${API}/api/b2b/pricing-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ...form, recaptcha_token }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Submission failed');
      }
      trackEvent('trade_pricing_requested', { project: form.project_name });
      setSubmitted(true);
      toast.success('Request sent — Oksana will reach out shortly.');
    } catch (err) {
      toast.error(err.message || 'Could not submit the request.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md"
          onClick={handleClose}
          data-testid="trade-pricing-modal-backdrop"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.97, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97 }}
            transition={{ duration: 0.25 }}
            onClick={(e) => e.stopPropagation()}
            className="relative bg-background w-full max-w-2xl max-h-[92vh] overflow-y-auto"
            data-testid="trade-pricing-modal"
          >
            {/* Заголовок формы */}
            <div className="sticky top-0 z-10 bg-background border-b border-border px-8 py-6 flex items-start justify-between">
              <div>
                <p className="text-[10px] tracking-[0.4em] uppercase text-muted-foreground mb-2">
                  {l.modalEyebrow || 'Trade Program'}
                </p>
                <h2 className="text-2xl md:text-3xl font-extralight tracking-tight">
                  {l.modalTitle || 'Request Custom Pricing'}
                </h2>
                <p className="text-sm text-muted-foreground font-light mt-2 max-w-md leading-relaxed">
                  {l.modalDesc || 'Pricing is tailored to each project. Share the brief and Oksana will revert within 24h with a custom quote.'}
                </p>
              </div>
              <button
                onClick={handleClose}
                className="p-2 hover:bg-muted transition-colors -mr-2"
                data-testid="trade-pricing-modal-close"
                aria-label="Close"
              >
                <X className="w-5 h-5" strokeWidth={1.5} />
              </button>
            </div>

            {/* Тело формы */}
            {submitted ? (
              <div className="px-8 py-16 text-center" data-testid="trade-pricing-success">
                <div className="w-16 h-16 mx-auto mb-6 border border-foreground flex items-center justify-center">
                  <Send className="w-6 h-6" strokeWidth={1.2} />
                </div>
                <h3 className="text-xl font-extralight mb-3">
                  {l.successTitle || 'Request received'}
                </h3>
                <p className="text-sm text-muted-foreground max-w-md mx-auto font-light leading-relaxed mb-8">
                  {l.successDesc || 'We will contact you within one business day. Thank you for choosing FEDYK.'}
                </p>
                <Button onClick={handleClose} variant="outline" className="px-8 rounded-none">
                  {l.close || 'Close'}
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="px-8 py-8 space-y-6">
                {/* Карточка идентификации партнера */}
                <div className="bg-muted/40 border border-border p-5">
                  <p className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-3 font-semibold">
                    {l.submittingAs || 'Submitting as'}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm font-light">
                    <div><span className="text-muted-foreground">{l.labelName || 'Name'}: </span>{user?.first_name} {user?.last_name}</div>
                    <div><span className="text-muted-foreground">{l.labelEmail || 'Email'}: </span>{user?.email}</div>
                    {user?.phone && <div><span className="text-muted-foreground">{l.labelPhone || 'Phone'}: </span>{user.phone}</div>}
                    {user?.company_name && <div><span className="text-muted-foreground">{l.labelCompany || 'Company'}: </span>{user.company_name}</div>}
                  </div>
                </div>

                {/* Поля спецификации проекта */}
                <div>
                  <label className="block text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-2">
                    {l.projectName || 'Project Name *'}
                  </label>
                  <input
                    type="text" required value={form.project_name} onChange={handleChange('project_name')}
                    placeholder={l.projectPlaceholder || 'e.g. Villa Brussels · Kitchen renovation'}
                    className="w-full px-0 py-3 border-0 border-b border-border bg-transparent focus:outline-none focus:border-foreground transition-colors text-base font-light rounded-none"
                    data-testid="trade-pricing-project-name"
                  />
                </div>

                <div>
                  <label className="block text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-2">
                    {l.productsInterest || 'Products / Items of Interest *'}
                  </label>
                  <textarea
                    required rows={2} value={form.products_of_interest} onChange={handleChange('products_of_interest')}
                    placeholder={l.productsPlaceholder || 'AIR LINE wardrobe system, INTEGRA hidden-mount doors, RADIAL kitchen…'}
                    className="w-full px-0 py-3 border-0 border-b border-border bg-transparent focus:outline-none focus:border-foreground transition-colors text-base font-light resize-none rounded-none"
                    data-testid="trade-pricing-products"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-2">
                      {l.quantity || 'Quantity *'}
                    </label>
                    <input
                      type="text" required value={form.quantity} onChange={handleChange('quantity')}
                      placeholder={l.quantityPlaceholder || '3 wardrobes, 12 doors…'}
                      className="w-full px-0 py-3 border-0 border-b border-border bg-transparent focus:outline-none focus:border-foreground transition-colors text-base font-light rounded-none"
                      data-testid="trade-pricing-quantity"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-2">
                      {l.deadline || 'Target Deadline'}
                    </label>
                    <input
                      type="text" value={form.target_deadline} onChange={handleChange('target_deadline')}
                      placeholder={l.deadlinePlaceholder || 'Q3 2026 / specific date'}
                      className="w-full px-0 py-3 border-0 border-b border-border bg-transparent focus:outline-none focus:border-foreground transition-colors text-base font-light rounded-none"
                      data-testid="trade-pricing-deadline"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-2">
                      {l.clientCompany || 'Client Company'}
                    </label>
                    <input
                      type="text" value={form.client_company} onChange={handleChange('client_company')}
                      placeholder={l.clientCompanyPlaceholder || 'End-client / project owner'}
                      className="w-full px-0 py-3 border-0 border-b border-border bg-transparent focus:outline-none focus:border-foreground transition-colors text-base font-light rounded-none"
                      data-testid="trade-pricing-client-company"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-2">
                      {l.clientLocation || 'Client City / Country'}
                    </label>
                    <input
                      type="text" value={form.client_country_city} onChange={handleChange('client_country_city')}
                      placeholder={l.clientLocationPlaceholder || 'Brussels, Belgium'}
                      className="w-full px-0 py-3 border-0 border-b border-border bg-transparent focus:outline-none focus:border-foreground transition-colors text-base font-light rounded-none"
                      data-testid="trade-pricing-location"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-2">
                    {l.budgetRange || 'Budget Range'}
                  </label>
                  <input
                    type="text" value={form.budget_range} onChange={handleChange('budget_range')}
                    placeholder={l.budgetPlaceholder || '€50–80k / approximate'}
                    className="w-full px-0 py-3 border-0 border-b border-border bg-transparent focus:outline-none focus:border-foreground transition-colors text-base font-light rounded-none"
                    data-testid="trade-pricing-budget"
                  />
                </div>

                <div>
                  <label className="block text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-2">
                    {l.notes || 'Additional Notes'}
                  </label>
                  <textarea
                    rows={3} value={form.notes} onChange={handleChange('notes')}
                    placeholder={l.notesPlaceholder || 'Materials, finishes, installation context, special requests…'}
                    className="w-full px-0 py-3 border-0 border-b border-border bg-transparent focus:outline-none focus:border-foreground transition-colors text-base font-light resize-none rounded-none"
                    data-testid="trade-pricing-notes"
                  />
                </div>

                <div className="pt-4 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
                  <p className="text-xs text-muted-foreground font-light">
                    {l.requiredHint || '* Required. Pricing is reviewed manually within 24 hours.'}
                  </p>
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="btn-primary min-w-[200px]"
                    data-testid="trade-pricing-submit"
                  >
                    {submitting ? (
                      <><Loader2 className="mr-2 w-4 h-4 animate-spin" strokeWidth={1.5} />{l.sending || 'Sending…'}</>
                    ) : (
                      <><Send className="mr-2 w-4 h-4" strokeWidth={1.5} />{l.sendRequest || 'Send Request'}</>
                    )}
                  </Button>
                </div>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default TradePricingRequestModal;